import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Col, Row } from 'reactstrap';

import globalTranslations from '../../../../i18n/globalTranslations';
import { createForm } from '../../../../models/Form';
import { createFormField } from '../../../../models/FormField';
import OrderCheckoutProduct from '../../../../models/order/checkout/OrderCheckoutProduct';
import { modelOf } from '../../../../prop-types';
import CheckoutStore from '../../../../store/CheckoutStore';
import UIStore from '../../../../store/UIStore';
import { getReferenceFieldValidators } from '../../../../util/checkoutFormUtils';
import {
  productCountValid,
  quantityValid,
} from '../../../../util/formValidators';
import Price from '../../../product/Price';
import CheckoutCartProductDetails from '../product/CheckoutCartProductDetails';
import CheckoutCartProductReference from '../product/CheckoutCartProductReference';
import CheckoutCartProductImage from '../product/CheckoutCartProductImage';
import CheckoutCartProductPrice from '../product/CheckoutCartProductPrice';
import CheckoutCartProductQuantity from '../product/CheckoutCartProductQuantity';
import CheckoutCartProductRemove from '../product/CheckoutCartProductRemove';

const CheckoutCartProductRow = ({
  checkoutStore,
  uiStore,
  product,
  onOrderUpdate,
  disabled,
  showProductCode,
  intl,
}) => {
  const { cancelOrder } = checkoutStore;
  const quantityFieldName = `quantity:${product.id}`;
  const referenceFieldName = `reference:${product.id}`;

  const form = useMemo(() => {
    const step = product.getQuantityStep();
    const max = 99999;
    const referenceField = product.reference_field;
    const referenceValidators = getReferenceFieldValidators(
      referenceField,
      intl.formatMessage(globalTranslations.referenceTitle)
    );

    const formFields = {
      [quantityFieldName]: createFormField(
        {
          name: quantityFieldName,
          value: product.quantity * step,
        },
        [quantityValid(max, step), productCountValid(step)]
      ),
      [referenceFieldName]: createFormField(
        {
          name: referenceFieldName,
          value: referenceField?.value || null,
          type: referenceField?.type,
          required: referenceField?.required ?? false,
        },
        referenceValidators,
        () => referenceField
      ),
    };
    return createForm(formFields);
  }, [product.quantity]);

  const updateOrder = (args) => onOrderUpdate(args);

  const removeProduct = () => {
    product.updateQuantity(0);
    updateOrder({ withoutTimer: true });
  };

  const countProductQuantity = (value) => value / product.getQuantityStep();

  const resetProductQuantity = () => {
    const quantityField = form.getField(quantityFieldName);
    if (quantityField.value !== '') return;
    quantityField.setValue(
      String(product.quantity * product.getQuantityStep())
    );
  };

  const updateProductQuantity = (value) => {
    const quantityField = form.getField(quantityFieldName);
    if (quantityField.value === '') {
      cancelOrder();
      return;
    }
    const newQuantity = countProductQuantity(quantityField.value);
    form.validateField(quantityFieldName);
    if (quantityField.valid) {
      product.updateQuantity(newQuantity);
      updateOrder({ customTimer: 800 });
    }
  };

  const unitPriceContent = (
    <>
      <Price
        className="CheckoutCartProductRow__product-price"
        price={product.unit_price_formatted}
        discount={product.has_discount}
      />
      {product.has_discount && (
        <Price
          price={product.unit_normal_price_formatted}
          muted
          fontSize
          prefix={
            <>
              <FormattedMessage {...globalTranslations.RRP} />{' '}
            </>
          }
          className="CheckoutCartProductRow__normal-product-price"
        />
      )}
    </>
  );

  const referenceField = form.getField(referenceFieldName);
  const quantityField = form.getField(quantityFieldName);

  const renderDesktopRow = () => (
    <>
      <Col xl={7}>
        <div className="CheckoutCartProductRow__product">
          <CheckoutCartProductImage product={product} />
          <CheckoutCartProductDetails
            product={product}
            unitPriceContent={unitPriceContent}
            showProductCode={false}
            showAvailability
          />
        </div>
      </Col>
      <Col xl={3} className="CheckoutCartProductRow__col-quantity">
        <div className="CheckoutCartProductRow__quantity-group">
          <CheckoutCartProductQuantity
            product={product}
            quantityField={quantityField}
            disabled={disabled}
            onUpdateProductQuantity={updateProductQuantity}
            onBlur={resetProductQuantity}
            onFocus={cancelOrder}
            customError={product.quantity_error}
          />
          <CheckoutCartProductReference
            referenceField={referenceField}
            product={product}
            onOrderUpdate={updateOrder}
          />
        </div>
      </Col>
      <Col>
        <Row className="CheckoutCartProductRow__price-column">
          <Col className="CheckoutCartProductRow__price-wrapper">
            <CheckoutCartProductPrice product={product} />
            <CheckoutCartProductRemove
              onRemove={removeProduct}
              disabled={disabled}
              ariaLabel={intl.formatMessage(globalTranslations.removeTitle)}
            />
          </Col>
          {showProductCode && (
            <Col xs={12} className="CheckoutCartProductRow__additional-info">
              <div className="CheckoutCartProductRow__product-code">
                {product.actual_code}
              </div>
            </Col>
          )}
        </Row>
      </Col>
    </>
  );

  const renderMobileRow = () => (
    <Col xs={12}>
      <div className="CheckoutCartProductRow__product--mobile">
        <div className="CheckoutCartProductRow__product-head--mobile">
          <CheckoutCartProductImage product={product} />
          <div className="CheckoutCartProductRow__product-head-content--mobile">
            <CheckoutCartProductDetails
              product={product}
              unitPriceContent={unitPriceContent}
              showProductCode={false}
              onlyNameAndUnitPrice
            />
            <CheckoutCartProductPrice product={product} />
          </div>
        </div>
        <div className="CheckoutCartProductRow__product-utility-actions--mobile">
          <CheckoutCartProductQuantity
            product={product}
            quantityField={quantityField}
            disabled={disabled}
            onUpdateProductQuantity={updateProductQuantity}
            onBlur={resetProductQuantity}
            onFocus={cancelOrder}
            customError={product.quantity_error}
          />
          <CheckoutCartProductRemove
            onRemove={removeProduct}
            disabled={disabled}
            ariaLabel={intl.formatMessage(globalTranslations.removeTitle)}
          />
        </div>
        <CheckoutCartProductReference
          referenceField={referenceField}
          product={product}
          onOrderUpdate={updateOrder}
        />
        {showProductCode && (
          <div className="CheckoutCartProductRow__additional-info">
            <div className="CheckoutCartProductRow__product-code">
              {product.actual_code}
            </div>
          </div>
        )}
        <CheckoutCartProductDetails
          product={product}
          unitPriceContent={unitPriceContent}
          showProductCode={false}
          showAvailability
          hideNameAndUnitPrice
        />
      </div>
    </Col>
  );

  const content = !uiStore.isLargest ? renderMobileRow() : renderDesktopRow();

  return <Row className="CheckoutCartProductRow">{content}</Row>;
};

CheckoutCartProductRow.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  product: modelOf(OrderCheckoutProduct).isRequired,
  intl: intlShape.isRequired,
  onOrderUpdate: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  showProductCode: PropTypes.bool,
};

export default injectIntl(
  inject('checkoutStore', 'uiStore')(observer(CheckoutCartProductRow))
);
